import { ApiService } from "./../../../../shared/services/api/api.service";
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from "@angular/forms";
import { CommonModule, DatePipe } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { addDays, startOfWeek, eachDayOfInterval, format } from "date-fns";
import { MatTabsModule, MatTabChangeEvent } from "@angular/material/tabs";
import { HttpHeaders } from "@angular/common/http";
import { Observable, Subject } from "rxjs";
import { Router } from "@angular/router";
import { Service } from "../../../../shared/models/service.model";
import { CustomerLoginComponent } from "../../login/login.component";
import { CustomerSignUpComponent } from "../../sign-up/sign-up.component";
import { VerifyOtpComponent } from "../../../landing/verify-otp-page/verify-otp-page.component";
import { LoaderComponent } from "../../../../shared/components/loader/loader.component";
import { API_ENDPOINTS } from "../../../../shared/services/api/api-endpoints";
import { EmptyComponent } from "../../../../shared/components/empty/empty.component";
import { environment } from "../../../../../environments/environment";

declare global {
  interface Window {
    Square?: any;
  }
}

export interface Staff {
  staff_id: string;
  full_name: string;
  job_title: string;
  email: string;
  specialization?: string;
  image?: string;
  rating?: number;
  experience?: string;
}

export interface TimeSlot {
  time: string;
  available: boolean;
}

export interface AvailabilityResponse {
  date: string;
  staff_id: string;
  duration_minutes: number;
  slots: TimeSlot[];
}

export interface SalonData {
  salon_id: string;
  name: string;
  description: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  is_active: boolean;
  services: Service[];
  staff: Staff[];
}

export interface UserData {
  user_id: string;
  email: string;
  full_name: string;
  role_id: number;
  role_name: string;
}

@Component({
  selector: "app-service-appointment",
  imports: [
    CommonModule,
    FormsModule,
    EmptyComponent,
    ReactiveFormsModule,
    DatePipe,
    MatIconModule,
    MatTabsModule,
    CustomerLoginComponent,
    CustomerSignUpComponent,
    VerifyOtpComponent,
    LoaderComponent,
  ],
  templateUrl: "./service-appointment.component.html",
  styleUrls: ["./service-appointment.component.scss"],
})
export class ServiceAppointmentComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input() showAppointmentModal: boolean = false;
  @Input() selectedService: Service | null = null;
  @Input() salonId = "a5c825a9-8158-49f5-a90b-a45b7fae68b3";
  @Input() salonServices: Service[] = [];
  @Input() salonStaff: Staff[] = [];

  @Output() closeModal = new EventEmitter<void>();
  @Output() appointmentBooked = new EventEmitter<any>();

  private readonly API_BASE = environment.apiUrl;
  assetUrl = environment.assetBaseUrl;

  currentStep = 1;
  isLoading = false;
  isLoggedIn = false;
  isRegisteredUser: boolean = true;
  bookingAuthView: "login" | "signup" | "otp" = "login";
  bookingAuthEmail = "";
  currentUser: UserData | null = null;

  readonly bookingSteps = [
    { number: 1, label: "Services" },
    { number: 2, label: "Professional" },
    { number: 3, label: "Date & Time" },
    { number: 4, label: "Account" },
    { number: 5, label: "Confirm" },
    { number: 6, label: "Complete" },
  ];

  get modalTitle(): string {
    switch (this.currentStep) {
      case 1:
        return "Choose Services";
      case 2:
        return "Choose a Professional";
      case 3:
        return "Select Available Time";
      case 4:
        return this.isLoggedIn
          ? "Continue with Your Account"
          : "Sign In to Continue";
      case 5:
        return "Review & Confirm";
      case 6:
        return "Booking Confirmed";
      default:
        return "Book Appointment";
    }
  }
  salonData: SalonData | null = null;
  currentWeekDays: any[] = [];
  startOfWeekDate: Date = startOfWeek(new Date(), { weekStartsOn: 1 });
  fillPrimaryDetails: boolean = false;

  activeTab: { index: number; label: string } = { index: 0, label: "Featured" };
  filteredServices: Service[] = [];
  serviceCategories: string[] = ["Featured", "Hair", "Esthetics", "Nail"];

  selectedDate: Date | null = null;
  formatedSelectedDate: string | null = null;
  selectedTime = "";
  selectedServices: Service[] = [];
  selectedStaff: Staff | null = null;

  services: Service[] = [];
  staff: Staff[] = [];
  availableDates: Date[] = [];
  availableTimeSlots: TimeSlot[] = [];
  loadingAvailability = false;

  // Full time grid shown every day.
  // Change these values if you want a wider/narrower booking window.
  private readonly bookingGridStartHour = 8;
  private readonly bookingGridEndHour = 20;
  private readonly bookingGridIntervalMinutes = 30;

  loginForm: FormGroup;
  loadingSalonData = false;
  loadingUserData = false;

  paymentForm = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  };

  paymentErrorMessage = "";
  squareCardReady = true;

  // Promotion state
  promoCode = "";
  promoApplying = false;
  promoError = "";
  appliedPromotion: any | null = null;
  discountAmount = 0;

  private destroy$ = new Subject<void>();

  private squarePayments: any | null = null;
  private squareCard: any | null = null;
  private squareInitialized = false;

  private squareAppId = environment.squareAppId;
  private squareLocationId = environment.squareLocationId;
  private useSquareSandbox = environment.squareUseSandbox ?? true;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private ApiService: ApiService,
  ) {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", [Validators.required, Validators.minLength(8)]],
    });
  }

  ngOnInit() {
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    this.tryInitSquareWhenNeeded();
  }

  ngOnChanges() {
    if (this.showAppointmentModal && this.salonId) {
      this.initializeComponent();
      this.tryInitSquareWhenNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.squareCard = null;
    this.squarePayments = null;
    this.squareInitialized = false;
  }

  private async initializeComponent() {
    this.generateAvailableDates();
    this.availableTimeSlots = this.buildFullTimeGrid();
    this.getWeekDays(this.startOfWeekDate);

    await this.checkUserLoginStatus();

    if (this.salonServices.length === 0 || this.salonStaff.length === 0) {
      await this.loadSalonData();
    } else {
      this.services = this.salonServices;
      this.staff = this.salonStaff;
      this.getFilteredServices(this.services);
    }

    if (this.selectedService) {
      this.selectedServices = [this.selectedService];
    }

    if (this.currentUser) {
      const nameParts = (this.currentUser.full_name || "").trim().split(" ");
      this.paymentForm.first_name = nameParts[0] || "";
      this.paymentForm.last_name =
        nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
      this.paymentForm.email = this.currentUser.email || "";
    }

    setTimeout(() => {
      this.tryInitSquareWhenNeeded();
    }, 0);
  }

  private async tryInitSquareWhenNeeded(): Promise<void> {
    const confirmStep = 5;

    if (!this.showAppointmentModal) return;
    if (this.currentStep !== confirmStep) return;
    if (this.squareInitialized) return;

    try {
      await this.ensureSquareSdkLoaded();
      await this.initSquareCard();
      this.squareCardReady = true;
      this.paymentErrorMessage = "";
    } catch (error: any) {
      console.error("Square init error:", error);
      this.squareCardReady = false;
      this.paymentErrorMessage =
        error?.message || "Failed to initialize payment form.";
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem("access_token");
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    });
  }

  private async checkUserLoginStatus(): Promise<void> {
    this.isLoading = true;

    try {
      const user = await this.getUserDetails().toPromise();
      if (user && user.user_id) {
        this.currentUser = user;
        this.isLoggedIn = true;
      }
    } catch (error) {
      this.isLoggedIn = false;
      this.currentUser = null;
    } finally {
      this.isLoading = false;
    }
  }

  onCheckboxChange(event: Event) {
    const inputTarget = event.target as HTMLInputElement;
    if (inputTarget.checked) {
      console.log("Filled with primary card details");
    }
  }

  private getUserDetails(): Observable<UserData> {
    return this.ApiService.request(API_ENDPOINTS.auth.me, "GET");
  }

  public isUserLoggedIn = (): void => {
    this.finishBookingAuthentication();
  };

  public handleRegisterUser = (): void => {
    this.bookingAuthView =
      this.bookingAuthView === "signup" ? "login" : "signup";
  };

  showBookingLogin(email?: string): void {
    if (email) {
      this.bookingAuthEmail = email;
    }
    this.bookingAuthView = "login";
  }

  showBookingSignup(email?: string): void {
    if (email) {
      this.bookingAuthEmail = email;
    }
    this.bookingAuthView = "signup";
  }

  onBookingOtpRequested(email: string): void {
    this.bookingAuthEmail = email;
    this.bookingAuthView = "otp";
  }

  onBookingAccountCreated(email: string): void {
    this.bookingAuthEmail = email;
    this.bookingAuthView = "login";
  }

  onBookingOtpVerified(): void {
    this.finishBookingAuthentication();
  }

  private async finishBookingAuthentication(): Promise<void> {
    await this.checkUserLoginStatus();

    if (!this.isLoggedIn || !this.currentUser) {
      return;
    }

    const nameParts = (this.currentUser.full_name || "").trim().split(" ");
    this.paymentForm.first_name = nameParts[0] || "";
    this.paymentForm.last_name =
      nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    this.paymentForm.email = this.currentUser.email || this.bookingAuthEmail;

    this.currentStep = 5;
    this.bookingAuthView = "login";

    setTimeout(() => this.tryInitSquareWhenNeeded(), 0);
  }

  private async loadSalonData(): Promise<void> {
    if (!this.salonId) {
      console.error("No salon ID provided");
      return;
    }

    this.isLoading = true;

    try {
      const salonData = await this.getSalonCatalog(this.salonId).toPromise();
      if (salonData) {
        this.salonData = salonData;
        this.services = salonData.services || [];
        this.staff = salonData.staff || [];
        this.extractServiceCategories();
        this.getFilteredServices(this.services);
      }
    } catch (error) {
      console.error("Error loading salon data:", error);
      this.initializeMockData();
    } finally {
      this.isLoading = false;
    }
  }

  public checkIsDateValid = (date: string): boolean => {
    let currentDate = new Date();
    let dateToCompare = new Date(date);
    currentDate.setHours(0, 0, 0, 0);
    dateToCompare.setHours(0, 0, 0, 0);

    if (dateToCompare < currentDate) {
      return false;
    }
    return true;
  };

  private getSalonCatalog(salonId: string): Observable<SalonData> {
    return this.ApiService.request(
      API_ENDPOINTS.public.salonCatalog(salonId),
      "GET",
    );
  }

  private extractServiceCategories(): void {
    const categories = new Set<string>(["Featured"]);

    this.services.forEach((service) => {
      let category = this.categorizeService(service.name);
      categories.add(category);
    });

    this.serviceCategories = Array.from(categories);
  }

  private categorizeService(serviceName: string): string {
    const name = serviceName.toLowerCase();

    if (
      name.includes("hair") ||
      name.includes("cut") ||
      name.includes("style") ||
      name.includes("color") ||
      name.includes("blonde")
    ) {
      return "Hair";
    } else if (
      name.includes("facial") ||
      name.includes("face") ||
      name.includes("skin")
    ) {
      return "Esthetics";
    } else if (
      name.includes("nail") ||
      name.includes("manicure") ||
      name.includes("pedicure")
    ) {
      return "Nail";
    }

    return "Hair";
  }

  getWeekDays(startOfWeek: Date): void {
    let weeklyDates = eachDayOfInterval({
      start: startOfWeek,
      end: addDays(startOfWeek, 6),
    });
    this.currentWeekDays = weeklyDates;
  }

  prev(): void {
    this.startOfWeekDate = addDays(this.startOfWeekDate, -7);
    this.getWeekDays(this.startOfWeekDate);
  }

  next(): void {
    this.startOfWeekDate = addDays(this.startOfWeekDate, 7);
    this.getWeekDays(this.startOfWeekDate);
  }

  onTabChange(event: MatTabChangeEvent) {
    const Index = event.index;
    const Label = event.tab.textLabel;
    this.activeTab["label"] = Label;
    this.activeTab["index"] = Index;
    this.getFilteredServices(this.services);
  }

  getFilteredServices(services: Service[]): void {
    let tab = this.activeTab["label"];
    let filteredArray = services.filter((item) => {
      if (tab === "Featured") {
        return true;
      }
      return this.categorizeService(item.name) === tab;
    });
    this.filteredServices = filteredArray;
  }

  private initializeMockData() {
    this.services = [
      {
        service_id: "1",
        name: "Haircut & Style",
        price: "45.00",
        duration: "1h, 30 mins",
        category: "Hair",
        created_at: "",
        updated_at: "",
      },
      {
        service_id: "2",
        name: "Hair Color",
        price: "85.00",
        duration: "30 mins",
        category: "Hair",
        created_at: "",
        updated_at: "",
      },
      {
        service_id: "3",
        name: "Facial Treatment",
        price: "65.00",
        duration: "1h, 30 mins",
        category: "Esthetics",
        created_at: "",
        updated_at: "",
      },
    ];

    this.staff = [
      {
        staff_id: "1",
        full_name: "Sarah Johnson",
        job_title: "Hair Stylist",
        email: "sarah@salon.com",
        specialization: "Hair Stylist",
        image: "/female-hairstylist.png",
        rating: 4.9,
        experience: "8 years",
      },
      {
        staff_id: "2",
        full_name: "Mike Chen",
        job_title: "Barber",
        email: "mike@salon.com",
        specialization: "Barber",
        image: "/male-barber.png",
        rating: 4.8,
        experience: "6 years",
      },
    ];

    this.getFilteredServices(this.services);
  }

  generateAvailableDates() {
    const dates = [];
    const today = new Date();

    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    this.availableDates = dates;
  }

  private buildFullTimeGrid(): TimeSlot[] {
    const slots: TimeSlot[] = [];

    const startMinutes = this.bookingGridStartHour * 60;
    const endMinutes = this.bookingGridEndHour * 60;

    for (
      let totalMinutes = startMinutes;
      totalMinutes <= endMinutes;
      totalMinutes += this.bookingGridIntervalMinutes
    ) {
      const hour24 = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      const period = hour24 >= 12 ? "PM" : "AM";
      const hour12 = hour24 % 12 || 12;

      slots.push({
        time: `${hour12}:${minutes.toString().padStart(2, "0")} ${period}`,
        available: false,
      });
    }

    return slots;
  }

  private normalizeTimeLabel(value: string): string {
    const trimmed = (value || "").trim().toUpperCase();

    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);

    if (!match) {
      return trimmed;
    }

    const hour = Number(match[1]);
    const minutes = Number(match[2] || "00");
    const period = match[3];

    return `${hour}:${minutes.toString().padStart(2, "0")} ${period}`;
  }

  private mergeAvailability(responseSlots: TimeSlot[]): TimeSlot[] {
    const availability = new Map<string, boolean>();

    for (const slot of responseSlots || []) {
      availability.set(this.normalizeTimeLabel(slot.time), !!slot.available);
    }

    return this.buildFullTimeGrid().map((slot) => ({
      ...slot,
      available: availability.get(this.normalizeTimeLabel(slot.time)) === true,
    }));
  }

  private loadAvailableTimeSlots(): void {
    // Keep the full grid visible at all times.
    // Before the API responds, every slot is disabled.
    this.availableTimeSlots = this.buildFullTimeGrid();

    if (
      !this.selectedDate ||
      !this.selectedStaff ||
      this.selectedServices.length === 0
    ) {
      this.selectedTime = "";
      return;
    }

    const date = format(this.selectedDate, "yyyy-MM-dd");
    const serviceIds = this.selectedServices
      .map((service) => service.service_id)
      .join(",");

    this.loadingAvailability = true;
    this.selectedTime = "";

    this.ApiService.request<AvailabilityResponse>(
      API_ENDPOINTS.public.staffAvailability(
        this.salonId,
        this.selectedStaff.staff_id,
        date,
        serviceIds,
      ),
      "GET",
    ).subscribe({
      next: (response) => {
        this.availableTimeSlots = this.mergeAvailability(response?.slots || []);
        this.loadingAvailability = false;
      },
      error: (error) => {
        console.error("Failed to load staff availability:", error);

        // If availability cannot be loaded, show the full grid disabled.
        this.availableTimeSlots = this.buildFullTimeGrid();
        this.selectedTime = "";
        this.loadingAvailability = false;
      },
    });
  }

  nextStep() {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.currentStep++;
    setTimeout(() => this.tryInitSquareWhenNeeded(), 0);
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  continueWithCurrentUser(): void {
    if (!this.isLoggedIn || !this.currentUser) {
      return;
    }

    this.currentStep = 5;
    setTimeout(() => this.tryInitSquareWhenNeeded(), 0);
  }

  async signOutAndSwitchAccount(): Promise<void> {
    this.isLoading = true;

    try {
      await this.ApiService.request(
        API_ENDPOINTS.auth.logout,
        "POST",
        {},
      ).toPromise();
    } catch (error) {
      console.warn("Logout request failed:", error);
    } finally {
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
      }

      this.currentUser = null;
      this.isLoggedIn = false;
      this.bookingAuthView = "login";
      this.bookingAuthEmail = "";
      this.loginForm.reset();

      this.paymentForm = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
      };

      this.squareCardReady = true;
      this.paymentErrorMessage = "";
      this.isLoading = false;
      this.currentStep = 4;
    }
  }

  validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.selectedServices.length > 0;
      case 2:
        return !!this.selectedStaff;
      case 3:
        return !!(this.selectedDate && this.selectedTime);
      case 4:
        return !!(this.isLoggedIn && this.currentUser);
      case 5:
        return this.isPaymentFormValid();
      default:
        return true;
    }
  }

  private isPaymentFormValid(): boolean {
    return !!(
      this.paymentForm.first_name?.trim() &&
      this.paymentForm.last_name?.trim() &&
      this.paymentForm.email?.trim()
    );
  }

  selectDate(date: Date): void {
    if (!this.checkIsDateValid(date.toString())) {
      return;
    }

    const dateOBJ = new Date(date);
    this.selectedDate = date;
    this.formatedSelectedDate = format(dateOBJ, "EEE, dd MMM yyyy");
    this.selectedTime = "";
    this.loadAvailableTimeSlots();
  }

  hasAvailableTimeSlots(): boolean {
    return this.availableTimeSlots.some((slot) => slot.available);
  }

  selectTime(time: string): void {
    const slot = this.availableTimeSlots.find((item) => item.time === time);
    if (!slot?.available) {
      return;
    }

    this.selectedTime = time;
  }

  toggleService(service: Service) {
    const index = this.selectedServices.findIndex(
      (s) => s.service_id === service.service_id,
    );
    if (index > -1) {
      this.selectedServices.splice(index, 1);
    } else {
      this.selectedServices.push(service);
    }

    // Services determine both staff compatibility and appointment duration.
    // A service change can invalidate a service-specific promotion,
    // so require the customer to apply the code again.
    this.removePromoCode();

    // Clear downstream selections whenever services change.
    this.selectedStaff = null;
    this.selectedDate = null;
    this.formatedSelectedDate = null;
    this.selectedTime = "";
    this.availableTimeSlots = this.buildFullTimeGrid();
  }

  isServiceSelected(serviceId: string): boolean {
    return this.selectedServices.some((s) => s.service_id === serviceId);
  }

  selectStaff(staff: Staff): void {
    this.selectedStaff = staff;
    this.selectedDate = null;
    this.formatedSelectedDate = null;
    this.selectedTime = "";
    this.availableTimeSlots = this.buildFullTimeGrid();
  }

  handleLogin() {
    if (this.loginForm.valid) {
      this.isLoggedIn = true;
      this.nextStep();
    }
  }

  switchToSignup() {
    console.log("Switch to signup");
  }

  async confirmAppointment() {
    this.paymentErrorMessage = "";

    if (!this.validateAppointmentData()) {
      console.error("Invalid appointment data");
      return;
    }

    if (!this.squareCard) {
      this.squareCardReady = false;
      this.paymentErrorMessage = "Payment form is not ready yet.";
      return;
    }

    this.isLoading = true;

    try {
      const tokenizeResult = await this.squareCard.tokenize();

      if (tokenizeResult.status !== "OK") {
        const message =
          tokenizeResult?.errors?.[0]?.message || "Unable to tokenize card.";
        throw new Error(message);
      }

      const sourceId = tokenizeResult.token;
      const appointmentData = this.buildAppointmentPayload(sourceId);

      const result = await this.createAppointment(appointmentData).toPromise();

      if (result) {
        this.appointmentBooked.emit(result);
        this.nextStep();
      }
    } catch (error: any) {
      console.error("Error creating appointment / payment:", error);
      this.paymentErrorMessage =
        error?.error?.message ||
        error?.message ||
        "Unable to process payment. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }

  private validateAppointmentData(): boolean {
    if (!this.selectedDate || !this.selectedTime) {
      console.error("Date and time must be selected");
      return false;
    }

    if (this.selectedServices.length === 0) {
      console.error("At least one service must be selected");
      return false;
    }

    if (!this.selectedStaff) {
      console.error("Staff must be selected");
      return false;
    }

    if (!this.currentUser?.user_id) {
      console.error("User must be logged in");
      return false;
    }

    if (!this.isPaymentFormValid()) {
      this.paymentErrorMessage = "Please complete the payment details.";
      return false;
    }

    return true;
  }

  private buildAppointmentPayload(sourceId: string): any {
    const startDateTime = this.buildDateTime(
      this.selectedDate!,
      this.selectedTime,
    );

    return {
      start_time: startDateTime,
      service_ids: this.selectedServices.map((s) => s.service_id),
      staff_id: this.selectedStaff?.staff_id,
      payment_status: "paid",
      payment_method: "card",
      payment_source_id: sourceId,

      // UI total only. The backend recalculates the authoritative price.
      payment_amount: this.getTotalPrice(),

      // Only send the promotion code. The backend revalidates the promotion
      // and recalculates original amount, discount, and final amount.
      promo_code: this.appliedPromotion?.code ?? null,

      payment_customer: {
        first_name: this.paymentForm.first_name,
        last_name: this.paymentForm.last_name,
        email: this.paymentForm.email,
        phone: this.paymentForm.phone,
      },
      notes: "Booking through web application",
    };
  }

  private buildDateTime(date: Date, time: string): string {
    const [timeStr, period] = time.split(" ");
    const [hours, minutes] = timeStr.split(":").map(Number);

    let hour24 = hours;
    if (period === "PM" && hours !== 12) {
      hour24 += 12;
    } else if (period === "AM" && hours === 12) {
      hour24 = 0;
    }

    const dateTime = new Date(date);
    dateTime.setHours(hour24, minutes, 0, 0);

    return dateTime.toISOString();
  }

  private createAppointment(appointmentData: any): Observable<any> {
    return this.ApiService.request(
      API_ENDPOINTS.customer.appointments.createForSalon(this.salonId),
      "POST",
      appointmentData,
      "Appointment created successfully!",
    );
  }

  getSubtotalPrice(): string {
    return this.getSubtotalAmount().toFixed(2);
  }

  getTotalPrice(): string {
    const subtotal = this.getSubtotalAmount();
    const total = Math.max(0, subtotal - this.discountAmount);
    return total.toFixed(2);
  }

  private getSubtotalAmount(): number {
    return this.selectedServices.reduce((sum, service) => {
      const price = Number(service?.price ?? 0);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
  }

  applyPromoCode(): void {
    const code = this.promoCode.trim().toUpperCase();

    if (!code) {
      this.promoError = "Enter a promotion code.";
      return;
    }

    if (!this.salonId) {
      this.promoError = "Salon information is unavailable.";
      return;
    }

    if (this.selectedServices.length === 0) {
      this.promoError =
        "Select at least one service before applying a promo code.";
      return;
    }

    const subtotal = this.getSubtotalAmount();

    this.promoApplying = true;
    this.promoError = "";
    this.appliedPromotion = null;
    this.discountAmount = 0;

    this.ApiService.request<any>(
      API_ENDPOINTS.customer.promoCode.validatePromo,
      "POST",
      {
        code,
        salon_id: this.salonId,
        service_ids: this.selectedServices.map((service) => service.service_id),
        amount: subtotal.toFixed(2),
      },
    ).subscribe({
      next: (response: any) => {
        const promotion =
          response?.promotion ?? response?.data?.promotion ?? null;

        const pricing = response?.pricing ?? response?.data?.pricing ?? null;

        if (response?.valid !== true || !promotion) {
          this.promoError =
            response?.error ||
            response?.message ||
            "This promotion could not be applied.";

          this.promoApplying = false;
          return;
        }

        const backendDiscount = Number(pricing?.discount_amount ?? 0);

        const backendFinalAmount = Number(pricing?.final_amount ?? subtotal);

        if (
          !Number.isFinite(backendDiscount) ||
          backendDiscount < 0 ||
          !Number.isFinite(backendFinalAmount) ||
          backendFinalAmount < 0
        ) {
          this.promoError =
            "The promotion returned invalid pricing information.";

          this.promoApplying = false;
          return;
        }

        this.appliedPromotion = promotion;
        this.promoCode = promotion?.code ?? code;
        this.discountAmount = backendDiscount;
        this.promoApplying = false;
      },

      error: (error: any) => {
        console.error("Failed to validate promotion:", error);
        console.error("PROMO ERROR BODY:", error?.error);
        console.error("PROMO ERROR STATUS:", error?.status);

        const errors = error?.error?.errors;

        if (Array.isArray(errors) && errors.length > 0) {
          this.promoError = errors.join(" ");
        } else {
          this.promoError =
            error?.error?.error ||
            error?.error?.detail ||
            error?.error?.message ||
            "Invalid promotion code.";
        }

        this.appliedPromotion = null;
        this.discountAmount = 0;
        this.promoApplying = false;
      },
    });
  }

  onPromoCodeChanged(): void {
    if (
      this.appliedPromotion &&
      this.promoCode.trim().toUpperCase() !==
        String(this.appliedPromotion?.code ?? "")
          .trim()
          .toUpperCase()
    ) {
      this.appliedPromotion = null;
      this.discountAmount = 0;
    }

    this.promoError = "";
  }

  removePromoCode(): void {
    this.promoCode = "";
    this.promoApplying = false;
    this.promoError = "";
    this.appliedPromotion = null;
    this.discountAmount = 0;
  }

  getPromotionDescription(): string {
    if (!this.appliedPromotion) {
      return "";
    }

    const type = String(this.appliedPromotion?.discount_type ?? "")
      .trim()
      .toUpperCase();

    const value = Number(this.appliedPromotion?.discount_value ?? 0);

    if (type === "PERCENT") {
      return `${value}% off · You save $${this.discountAmount.toFixed(2)}`;
    }

    return `$${value.toFixed(2)} off`;
  }

  getTotalDuration(): string {
    let totalMinutes = 0;

    this.selectedServices.forEach((service) => {
      if (service.duration) {
        const duration = service.duration.toLowerCase();

        const hourMatch = duration.match(/(\d+)h/);
        if (hourMatch) {
          totalMinutes += parseInt(hourMatch[1]) * 60;
        }

        const minMatch = duration.match(/(\d+)\s*min/);
        if (minMatch) {
          totalMinutes += parseInt(minMatch[1]);
        }
      } else {
        totalMinutes += 45;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  }

  closeModalHandler() {
    this.closeModal.emit();
    this.resetModal();
  }

  bookAnotherAppointment() {
    this.resetModal();
    this.currentStep = 1;
  }

  resetModal() {
    this.currentStep = 1;
    this.isLoading = false;
    this.selectedDate = null;
    this.selectedTime = "";
    this.selectedServices = [];
    this.selectedStaff = null;
    this.availableTimeSlots = this.buildFullTimeGrid();
    this.loadingAvailability = false;
    this.loginForm.reset();
    this.isRegisteredUser = true;
    this.bookingAuthView = "login";
    this.bookingAuthEmail = "";
    this.startOfWeekDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    this.activeTab = { index: 0, label: "Featured" };

    this.paymentForm = {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    };

    this.paymentErrorMessage = "";
    this.squareCardReady = true;

    this.promoCode = "";
    this.promoApplying = false;
    this.promoError = "";
    this.appliedPromotion = null;
    this.discountAmount = 0;

    this.squareInitialized = false;
    this.squareCard = null;
    this.squarePayments = null;
  }

  getStaffDisplayName(staff: Staff): string {
    return staff.full_name || "Unknown";
  }

  getStaffSpecialization(staff: Staff): string {
    return staff.specialization || staff.job_title || "Professional";
  }

  GoToDashboard = () => {
    let activeRoute = this.router.url;

    if (activeRoute.includes("/customer")) {
      this.router.navigate(["/admin/customer/overview"]);
    } else if (activeRoute.includes("/business")) {
      this.router.navigate(["/admin/business/overview"]);
    } else if (activeRoute.includes("/staff")) {
      this.router.navigate(["/admin/staff/overview"]);
    } else {
      this.router.navigate([""]);
    }

    this.closeModalHandler();
  };

  private async ensureSquareSdkLoaded(): Promise<void> {
    if (window.Square) return;

    const src = this.useSquareSandbox
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";

    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Square SDK failed to load."));
      document.head.appendChild(script);
    });
  }

  private async initSquareCard(): Promise<void> {
    if (!window.Square) {
      throw new Error("Square SDK is not available.");
    }

    if (!this.squareAppId || !this.squareLocationId) {
      throw new Error("Missing Square configuration.");
    }

    const container = document.getElementById("square-booking-card-container");
    if (!container) {
      throw new Error("Square card container not found.");
    }

    if (container.childNodes.length > 0) {
      this.squareInitialized = true;
      return;
    }

    this.squarePayments = window.Square.payments(
      this.squareAppId,
      this.squareLocationId,
    );

    this.squareCard = await this.squarePayments.card();
    await this.squareCard.attach("#square-booking-card-container");
    this.squareInitialized = true;
  }
}
